import 'bootstrap/dist/css/bootstrap.css';

import React from 'react';
import ReactDOM from 'react-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import { BrowserRouter } from 'react-router-dom';
import { App } from './App';
import { MsalProvider } from '@azure/msal-react';
import {
  PublicClientApplication,
  EventType,
} from '@azure/msal-browser';
import axios from 'axios';

import { buildMsalConfig, buildLoginRequest } from './utils/AuthConfig';
import { initializeAuthService } from './utils/AuthService';
import { IAzureADSettings } from './models/IAzureADSettings';
import { LoadingSpinner } from './components/LoadingSpinner';

const rootElement = document.getElementById('root');

// Render a loading spinner while we fetch configuration
ReactDOM.render(<LoadingSpinner />, rootElement);

async function main() {
  try {
    // Fetch auth settings from the unauthenticated endpoint
    const response = await axios.get<IAzureADSettings>('/api/WHCPortal/GetAzureADSettings');
    const authSettings = response.data;

    // Build configs
    const msalConfig = buildMsalConfig(authSettings);
    const loginRequest = buildLoginRequest(authSettings);

    // Create and initialize MSAL instance
    const msalInstance = new PublicClientApplication(msalConfig);
    msalInstance.addEventCallback((event: any) => {
      if (event.eventType === EventType.LOGIN_SUCCESS && event.payload.account) {
        const account = event.payload.account;
        msalInstance.setActiveAccount(account);
        // Reload the page to update the UI after login
        window.location.replace('/');
      }
      
      if (event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS && event.payload.account) {
        const activeAccount = msalInstance.getActiveAccount();
        if (
          !activeAccount ||
          activeAccount.homeAccountId !== event.payload.account.homeAccountId
        ) {
          msalInstance.setActiveAccount(event.payload.account);
          // Reload the page to update the UI after login
          window.location.replace('/');
        }
      }

      if (event.eventType === EventType.LOGOUT_END) {
        // Reload the page to update the UI after logout
        window.location.replace('/');
      }
    });

    // Initialize services
    initializeAuthService(msalInstance, loginRequest);

    const queryClient = new QueryClient();
    const baseUrl = document.getElementsByTagName('base')[0].getAttribute('href') || '';

    // Render the main app
    ReactDOM.render(
      <React.StrictMode>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter basename={baseUrl}>
            <MsalProvider instance={msalInstance}>
              <App />
            </MsalProvider>
          </BrowserRouter>
          <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
      </React.StrictMode>,
      rootElement
    );
  } catch (error) {
    console.error("Failed to initialize application:", error);
    // A simple error message for the user
    const errorDisplay = (
        <div style={{ padding: '20px', textAlign: 'center' }}>
            <h1>Application Error</h1>
            <p>Failed to load application configuration.</p>
            <p>Please try again later or contact support.</p>
        </div>
    );
    ReactDOM.render(errorDisplay, rootElement);
  }
}

main();